import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid2 as Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { listContacts, deleteContact, updateContact, filterContacts } from './Contact.service';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from "@mui/material/TableCell";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#10374A",
        color: theme.palette.common.white,
        fontSize: '0.875rem',
        fontWeight: 600,
        padding: theme.spacing(1, 2),
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: theme.spacing(1, 2),
    },
}));

const ContactTable: React.FC = () => {
    const [contacts, setContacts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<any>(null);
    const [statusFilter, setStatusFilter] = useState('All');

    const fetch = async () => {
        setLoading(true);
        try {
            let res: any;
            if (statusFilter === 'All') {
                res = await listContacts();
            } else {
                res = await filterContacts(statusFilter);
            }
            setContacts(res || []);
        } catch (err) {
            console.error(err);
            setContacts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetch();
    }, [statusFilter]);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this contact record?')) return;
        try {
            await deleteContact(id);
            fetch();
        } catch (err) {
            console.error(err);
            alert('Delete failed');
        }
    };

    const handleEditOpen = (contact: any) => {
        setSelectedContact({ ...contact });
        setEditOpen(true);
    };

    const handleEditSave = async () => {
        try {
            await updateContact(selectedContact._id, {
                status: selectedContact.status,
                reason: selectedContact.reason
            });
            setEditOpen(false);
            fetch();
        } catch (err) {
            console.error(err);
            alert('Update failed');
        }
    };

    return (
        <Box sx={{ paddingY: 6, width: '100%' }}>
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>Contact Submissions</h2>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Filter by Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Filter by Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value="All">All </MenuItem>
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="Contacted">Contacted</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Closed">Closed</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ marginTop: "20px", borderRadius: 1, boxShadow: 1, overflow: 'hidden' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Email</StyledTableCell>
                            <StyledTableCell>Phone</StyledTableCell>
                            <StyledTableCell>Subject</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell>Created At</StyledTableCell>
                            <StyledTableCell sx={{ textAlign: 'center' }}>Action</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contacts.map((c) => (
                            <TableRow key={c._id}>
                                <TableCell>{c.name}</TableCell>
                                <TableCell>{c.email}</TableCell>
                                <TableCell>{c.phone}</TableCell>
                                <TableCell>{c.subject}</TableCell>
                                <TableCell>
                                    <Box sx={{
                                        px: 1, py: 0.5,
                                        borderRadius: 1,
                                        display: 'inline-block',
                                        backgroundColor: c.status === 'New' ? '#DBEAFE' : c.status === 'Contacted' ? '#FFEDD5' : c.status === 'In Progress' ? '#F3E8FF' : '#DCFCE7',
                                        color: c.status === 'New' ? '#1E40AF' : c.status === 'Contacted' ? '#9A3412' : c.status === 'In Progress' ? '#6B21A8' : '#166534',
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold',
                                    }}>
                                        {c.status}
                                    </Box>
                                </TableCell>
                                <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleEditOpen(c)}
                                            sx={{
                                                backgroundColor: "#10374A",
                                                color: "#FFF",
                                                textTransform: 'none',
                                                '&:hover': { backgroundColor: "#08293D" }
                                            }}
                                        >
                                            Details
                                        </Button>
                                        <IconButton size="small" onClick={() => handleDelete(c._id)} sx={{
                                            backgroundColor: "#10374A",
                                            color: "#FFFFFF",
                                            minWidth: 32,
                                            minHeight: 32,
                                            "&:hover": { backgroundColor: "#08293D" }
                                        }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Details Dialog */}
            <Dialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                    }
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Contact Review</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedContact && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ backgroundColor: "#F8FAFC", p: 2, borderRadius: 1, mb: 3, border: "1px solid #E2E8F0" }}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>User Message</Typography>
                                <Typography variant="body1" sx={{ color: "#334155", whiteSpace: 'pre-wrap' }}>
                                    {selectedContact.message}
                                </Typography>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={selectedContact.status}
                                            label="Status"
                                            onChange={(e) => setSelectedContact({ ...selectedContact, status: e.target.value })}
                                        >
                                            <MenuItem value="New">New</MenuItem>
                                            <MenuItem value="Contacted">Contacted</MenuItem>
                                            <MenuItem value="In Progress">In Progress</MenuItem>
                                            <MenuItem value="Closed">Closed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label="Internal Notes / Reason"
                                        placeholder="Add notes about this contact..."
                                        multiline
                                        rows={4}
                                        value={selectedContact.reason || ''}
                                        onChange={(e) => setSelectedContact({ ...selectedContact, reason: e.target.value })}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleEditSave}
                        sx={{
                            backgroundColor: "#10374A",
                            textTransform: 'none',
                            '&:hover': { backgroundColor: "#08293D" }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ContactTable;
